import React, { useState, useEffect } from "react";
import { Product } from "../types/product.tsx";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";

interface ProductDetailsProps {
	product: Product | null;
	onClose: () => void;
}

const ProductDetails = ({ product, onClose }: ProductDetailsProps) => {
	const isMobile = useMediaQuery({ maxWidth: 767 });
	const [currentIndex, setCurrentIndex] = useState(0);

	useEffect(() => {
		setCurrentIndex(0);
	}, [product?.id]);

	if (!product) return null;

	const nextImage = () => {
		setCurrentIndex((prev) => (prev + 1) % product.images.length);
	};

	const prevImage = () => {
		setCurrentIndex((prev) =>
			prev === 0 ? product.images.length - 1 : prev - 1
		);
	};

	return (
		<AnimatePresence>
			{product && (
				<motion.div
					className="detail-wrapper"
					initial={{ opacity: 0, y: 100, x: "-50%" }}
					animate={{ opacity: 1, y: 0, x: "-50%" }}
					exit={{ opacity: 0, y: 100, x: "-50%" }}
				>
					<div className="main-img card-front" style={{ position: "relative" }}>
						{product.images.length > 0 ? (
							<div className="custom-slider" data-mobile={isMobile}>
								<AnimatePresence mode="wait">
									<motion.img
										key={currentIndex}
										src={product.images[currentIndex]}
										alt={`${product.name} ${currentIndex + 1}`}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.4 }}
										style={{
											width: "100%",
											height: "100%",
											display: "block",
											objectFit: "cover"
										}}
									/>
								</AnimatePresence>

								{/* left/right arrow */}
								{product.images.length > 1 && (
									<>
										<button
											onClick={prevImage}
											style={{
												position: "absolute",
												left: "8px",
												top: "50%",
												transform: "translateY(-50%)",
												background: "rgba(0,0,0,0.4)",
												color: "#fff",
												border: "none",
												borderRadius: "50%",
												width: "28px",
												height: "28px",
												cursor: "pointer",
												fontSize: "18px"
											}}
										>
											‹
										</button>
										<button
											onClick={nextImage}
											style={{
												position: "absolute",
												right: "8px",
												top: "50%",
												transform: "translateY(-50%)",
												background: "rgba(0,0,0,0.4)",
												color: "#fff",
												border: "none",
												borderRadius: "50%",
												width: "28px",
												height: "28px",
												cursor: "pointer",
												fontSize: "18px"
											}}
										>
											›
										</button>
									</>
								)}
							</div>
						) : (
							<img
								className="custom-slider img_single"
								data-mobile={isMobile}
								src={product.thumbnail}
								alt={product.name}
							/>
						)}
					</div>

					<div className="detail-container">
						<div className="detail-title">
							<span id="about">
								About<h3> {product.name}</h3>, Availability{" "}
								<span className="available">
									{product.available ? "🟢" : "🚫"}
								</span>
							</span>
							<button className="close-detail" onClick={onClose}>X</button>
						</div>

						<ul id="info">
							<li><span>Age</span> {product.age}</li>
							<li><span>Ethnicity</span> {product.ethnicity}</li>
							<li><span>Size</span> {product.spec}</li>
							<li><span>Location</span> {product.location}</li>
							<li>
								<span>Description</span><br />
								{product.description}
							</li>
						</ul>

						<div id="price">
							<div className="price-title">Price</div>
							<ul id="price-range">
								{product.cost.map((opt, idx) => (
									<li key={idx}>
										{opt.extra ? (
											<div>
												<div className="price-title">{opt.label}</div>
												<ul style={{ listStyleType: "disc", paddingLeft: "20px", margin: "4px 0 10px 0" }}>
													{opt.extra.map((item, i) => (
														<li key={i} style={{ marginBottom: "2px" }}>{item}</li>
													))}
												</ul>
											</div>
										) : (
											<>{opt.label} - ${opt.price}</>
										)}
									</li>
								))}
							</ul>
						</div>

						<div id="container-book">
							<a href="tel:604-210-8319" className="booking" data-book={product.available ? "available" : "unavailable"}>
								{product.available ? "Book now" : "Unavailable"}
							</a>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default ProductDetails;